"use client";
import { GAME_ID_SEPARATOR, IMAGE_SIZES } from "@/config/general";
import { I18N_NAMESPACE } from "@/config/i18n";
import { GAME_PLAY_ACTION, isActionNeedProfileUpdate } from "@/config/profile";
import { isFavoriteGame, wrapSize } from "@/fe-core/helpers/game";
import classNames from "classnames";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { usePathname, useRouter } from "../../navigation";
import { FC, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useScreen } from "../../context/ScreenContext";
import { setShowGameAlterModal } from "../../fe-core-omega/_redux/actions/casinoActions";
import playIcon from "/public/img/icons/circle-play.svg";
import {
  addFavoriteRequest,
  deleteFavoriteRequest,
  setShowProfileUpdateModal,
} from "../../fe-core-omega/_redux/actions/userActions";
import { isUserAuthenticatedSelector } from "../../fe-core-omega/_redux/selectors/sessionSelectors";
import {
  isUserProfileNeedToUpdateSelector,
  userFavoritesDataSelector,
  userFavoritesPendingSelector,
} from "../../fe-core-omega/_redux/selectors/userSelectors";
import {
  ICasinoGameViewModel,
  ICasinoGroupGameViewModel,
} from "../../fe-core-omega/meta/interfaces/casino";
import { IFavoriteGameViewModel } from "../../fe-core-omega/meta/interfaces/user";
import { localStorageKeys } from "../../fe-core-omega/meta/types/localStorage";
import useTracking from "../Hooks/useTracking";
import SVGIcon from "../SVGIcon";
import LiveLobbyDetails from "./LiveLobbyDetails";

const GameCard: FC<{
  game: ICasinoGameViewModel;
  hidePlayButton?: boolean;
  showGameDetails?: boolean;
  showFeaturedThumbnail?: boolean;
  featuredThumbnailWidth?: number;
  featuredThumbnailHeight?: number;
}> = ({
  game,
  hidePlayButton = false,
  showGameDetails = false,
  showFeaturedThumbnail = false,
  featuredThumbnailWidth = 0,
  featuredThumbnailHeight = 0,
}) => {
  const { click, track } = useTracking();
  const dispatch = useDispatch();
  const t = useTranslations(I18N_NAMESPACE.GAME_CARD);
  const isUserAuthenticated = useSelector(isUserAuthenticatedSelector);
  const favorites = useSelector(userFavoritesDataSelector);
  const pendingFavorites = useSelector(userFavoritesPendingSelector);

  const userProfileNeedsToUpdate = useSelector(
    isUserProfileNeedToUpdateSelector
  );

  const router = useRouter();

  const { isMobile } = useScreen();
  const pathname = usePathname() ?? "";
  const platformCode = useMemo(
    () => game?.platformCode || game?.productCode,
    [game]
  );

  const isFavorite = useMemo(
    () =>
      isFavoriteGame({
        games: favorites?.games?.items || [],
        gameId: game?.gameId,
        productCode: platformCode,
      }),
    [favorites, game]
  );

  const isLiveGame = useMemo(() => {
    return game?.liveCasinoCategory && game?.details;
  }, [game]);

  const gameBadges = () => {
    const badges: JSX.Element[] = [];

    if (game?.isNew) {
      badges.push(
        <span
          className="rounded bg-gradient-to-r from-error-500 to-warning-500 px-2.5 pt-[5px] pb-[3px] text-xxs font-bold leading-none uppercase text-white tracking-[1px]"
          key="new"
        >
          {t("new-tag")}
        </span>
      );
    }

    return badges;
  };

  const handleFavoriteButtonClick = () => {
    if (pendingFavorites) {
      return;
    }

    if (isFavorite) {
      dispatch(
        deleteFavoriteRequest({
          platformCode: platformCode,
          gameId: game?.gameLaunchId,
        })
      );
      click(`remove favorite game`, {
        gameName: game?.name,
        isLiveGame: isLiveGame ? "Yes" : "No",
        isNewGame: game?.isNew ? "Yes" : "No",
        gameId: game?.id?.toString(),
        component: "GameCard",
      });
    } else {
      dispatch(
        addFavoriteRequest({
          platformCode: platformCode,
          gameId: game?.gameLaunchId,
        })
      );
      click(`add favorite game`, {
        gameName: game?.name,
        isLiveGame: isLiveGame ? "Yes" : "No",
        isNewGame: game?.isNew ? "Yes" : "No",
        gameId: game?.id?.toString(),
        component: "GameCard",
      });
    }
  };

  const favoriteButton = () => {
    if (!isUserAuthenticated) {
      return null;
    }

    return (
      <div
        className={
          // !game?.liveCasinoCategory ? "absolute top-1 right-1 z-[2]" :
          "absolute top-2 left-2 z-[2]"
        }
        onClick={handleFavoriteButtonClick}
      >
        <SVGIcon
          icon={isFavorite ? "star" : "staroutlined"}
          className={classNames(
            "w-5 h-5 text-white transition-all transform hover:scale-125",
            { "text-[#FF0044]": isFavorite }
          )}
        />
      </div>
    );
  };

  const handlePlayMode = (hasFunMode: boolean) => {
    startTheGamePlay();
    if (!isUserAuthenticated && !isMobile) {
      click(`play game`, {
        gameName: game?.name,
        isLiveGame: isLiveGame ? "Yes" : "No",
        isNewGame: game?.isNew ? "Yes" : "No",
        gameId: game?.id?.toString(),
        isUserAuthenticated: isUserAuthenticated ? "Yes" : "No",
        isFunModeSelected: "Yes",
        isFunModeAvailable: game?.hasFunMode ? "Yes" : "No",
        component: "GameCard",
      });
      // dispatch(setShowGameAlterModal({ hasFunMode }));
    }

    // if (!game?.hasFunMode && !isUserAuthenticated) return;

    // startTheGamePlay();
  };

  const startTheGamePlay = () => {
    if (!pathname.includes("/casino/game/")) {
      localStorage.setItem(localStorageKeys.PREV_PATH, pathname);
    }
    if (isUserAuthenticated) {
      track(`start the game play`, {
        gameName: game?.name,
        isLiveGame: isLiveGame ? "Yes" : "No",
        isNewGame: game?.isNew ? "Yes" : "No",
        gameId: game?.id?.toString(),
        isUserAuthenticated: isUserAuthenticated ? "Yes" : "No",
        isUserProfileUpdated: userProfileNeedsToUpdate ? "No" : "Yes",
        isFunModeSelected: isUserAuthenticated ? "No" : "Yes",
        isFunModeAvailable: game?.hasFunMode ? "Yes" : "No",
        gameUrl: `/casino/game/${game.gameId}${GAME_ID_SEPARATOR}${
          game.productCode
        }?funMode=${isUserAuthenticated ? "false" : "true"}`,
        component: "GameCard",
      });
    }
    router.push(
      `/casino/game/${game.gameId}${GAME_ID_SEPARATOR}${game.productCode}${
        isUserAuthenticated ? "?funMode=false" : ""
      }`
    );
  };

  const gameCard = () => {
    const imageUrl = game?.primaryThumbnail
      ? game?.primaryThumbnail
      :  (game?.imageUrl ? wrapSize(game?.imageUrl, IMAGE_SIZES.MEDIUM) : "/img/dummythumb_mobile.jpg");
    return (
      <div className="relative overflow-visible">
        <div
          className="rounded group transition-all block cursor-pointer bg-white dark:bg-tertiary-600 relative overflow-hidden border border-[#353535]"

          // onClick={openPlayOptions}
        >
          <div className="relative ">
            <div className="absolute z-[1] rounded bottom-1 left-1">
              {gameBadges()}
            </div>
            {favoriteButton()}
            <div className="absolute inset-0   items-center justify-center z-[1] w-full h-full transition-all transform opacity-0 group-hover:opacity-100  bg-tertiary-600/50 group-hover:bg-opacity-80 bg-opacity-0 duration-300 flex-col  lg:flex hidden">
              <div
                className="scale-90 group-hover:scale-100 hover:!scale-125 transition-all delay-75"
                onClick={() => handlePlayMode(game?.hasFunMode)}
              >
                <Image src={playIcon} className="lg:max-h-none max-h-8" loading="lazy"/>
              </div>{" "}
            </div>
            <div
              className={`relative overflow-hidden ${
                isMobile ? "aspect-[428/330]" : "aspect-[428/330]"
              }`}
              onClick={() => {
                handlePlayMode(game?.hasFunMode);
              }}
              style={
                showFeaturedThumbnail
                  ? {
                      aspectRatio: `${featuredThumbnailWidth}/${featuredThumbnailHeight}`,
                    }
                  : {}
              }
            >
              <Image
                src={
                  showFeaturedThumbnail
                    ? game?.featuredThumbnail || imageUrl
                    : imageUrl
                }
                alt={game?.name}
                fill
                sizes="max-width:100%"
                className="object-cover object-center w-full h-full"
                priority={true}
              />
            </div>
          </div>
          {(game?.liveCasinoCategory && game?.details) || showGameDetails ? (
            <div className="flex flex-col gap-4 p-3">
              {game?.liveCasinoCategory && game?.details && (
                <div
                  onClick={() => {
                    handlePlayMode(game?.hasFunMode);
                  }}
                >
                  <LiveLobbyDetails game={game} />
                </div>
              )}
              {!game?.liveCasinoCategory && (
                <div
                  onClick={() => {
                    handlePlayMode(game?.hasFunMode);
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <h6
                      title={game?.name}
                      className="overflow-hidden text-sm font-normal text-tertiary dark:text-white overflow-ellipsis whitespace-nowrap"
                    >
                      {game?.name}
                    </h6>
                  </div>
                </div>
              )}
              {!hidePlayButton && (
                <div
                  onClick={() => {
                    handlePlayMode(game?.hasFunMode);
                  }}
                >
                  <button
                    onClick={() => {
                      handlePlayMode(game?.hasFunMode);
                    }}
                    className="flex-none !rounded btn btn--primary btn--sm sm:btn--lg w-fit sm:!rounded"
                  >
                    {t("play-now")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <></>
          )}
        </div>

        <div className="flex flex-col items-center justify-center gap-1 pb-1 mt-2 dark:text-white">
          <h6 className="w-full overflow-hidden text-sm font-bold text-center text-tertiary dark:text-white overflow-ellipsis whitespace-nowrap">
            {game?.name}
          </h6>
          <p className="leading-none opacity-50 text-xxs">
            {game?.subVendor?.displayName}
          </p>
        </div>

        {/*{game?.liveCasinoCategory && (*/}
        {/*  <div className="flex flex-col items-center justify-center gap-1 pb-1 mt-2 dark:text-white">*/}
        {/*    <h6*/}
        {/*      title={game?.name}*/}
        {/*      className="w-full overflow-hidden text-sm font-bold text-center text-tertiary dark:text-white overflow-ellipsis whitespace-nowrap"*/}
        {/*    >*/}
        {/*      {game?.name}*/}
        {/*    </h6>*/}
        {/*    <p className="leading-none opacity-50 text-xxs">*/}
        {/*      {game?.subVendor}*/}
        {/*    </p>*/}
        {/*  </div>*/}
        {/*)}*/}
      </div>
    );
  };

  return <>{gameCard()} </>;
};

export default GameCard;
